import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { useWorkspace } from '@/features/workspaces/helpers';
import { BidBreadcrumbs } from '@/features/bids/components/bid-breadcrumbs';
import { createFileRoute } from '@tanstack/react-router';
import z from 'zod';
import { PlusSignIcon, FileDownloadIcon } from '@hugeicons-pro/core-stroke-rounded';
import { Icon } from '@/ui/components/icon';
import { useQuery } from '@tanstack/react-query';
import { useProjectView } from '@/features/projects/helpers';
import { BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderChatButton } from '@/ui/components/header';
import {
  Menu,
  MenuGroup,
  MenuItem,
  MenuPopup,
  MenuScrollArea,
  MenuSeparator,
  MenuTrigger,
  MenuTriggerIcon,
} from '@/ui/components/menu';
import {
  bidMethodologySummaryQuery,
  bidMethodologyVersionsQuery,
} from '@/features/bid-methodology/queries';
import { Spinner } from '@/ui/components/spinner';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { MainHeader, MainHeaderBackButton, MainScrollArea } from '@/ui/components/main';
import { MethodologyDocumentTabs } from '@/features/bid-methodology/components/methodology-document-tabs';
import { entityDocumentsQuery, useDocumentsIndexingStatus } from '@/features/documents/queries';
import {
  useAllStatementsForVersion,
  useDraftVersionOffset,
} from '@/features/bid-methodology/helpers';
import {
  buildSuggestionItems,
  formatVersionLabel,
  isRunningVersion,
  statementDocId,
  liveMethodologyVersion,
} from '@/features/bid-methodology/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import { MethodologyEditor } from '@/features/bid-methodology/components/methodology-editor';
import { CreateNewVersion } from '@/features/bid-methodology/components/create-new-version';
import { REFETCH_SUMMARIZATION_INTERVAL, AnalysisStatus } from '@/features/workspaces/constants';
import { Tabs, TabsList, TabsPanel, TabsTab } from '@/ui/components/tabs';
import { BidMethodologyOverview } from '@/features/bid-methodology/components/bid-methodology-overview';
import { METHODOLOGY_TABS, SCORE_VIEW_TABS } from '@/features/bid-methodology/constants';
import { H1 } from '@/ui/components/heading';
import { StaticFormattingToolbar } from '@/ui/components/editor/static-formatting-toolbar';
import { useExportBidMethodologyDocument } from '@/features/bid-methodology/mutations';
import {
  bidMethodologyEditorAtom,
  bidMethodologyEditorDocIdAtom,
  selectedSuggestionIdAtom,
} from '@/features/bid-methodology/store';
import { Button } from '@/ui/components/button';
import { BadgeDot } from '@/ui/components/badge';
import { useEffect, useState } from 'react';
import { Main } from '@/ui/components/main';
import { VersionHistoryPanel } from '@/features/workspaces/components/version-history-panel';
import { useNoDocumentsToast } from '@/features/documents/helpers';
import { useBidMethodologyChat } from '@/features/bid-methodology/helpers';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/bids/$bidId/methodology',
)({
  staticData: { chatSurface: 'bid_methodology' },
  component: RouteComponent,
  validateSearch: z.object({
    tab: z.enum(METHODOLOGY_TABS).default('review').catch('review'),
    scoreView: z.enum(SCORE_VIEW_TABS).default('score').catch('score'),
    version: z.string().optional(),
    docId: z.string().optional(),
    statementId: z.string().optional(),
    commentThreadId: z.string().optional(),
  }),
});

function RouteComponent() {
  const { bidId, workspaceId, projectId } = Route.useParams();
  const navigate = useNavigate();

  useNoDocumentsToast({
    resourceId: bidId,
    documentType: 'bid',
    onGoToFiles: () =>
      navigate({
        to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/files',
        params: { projectId, workspaceId, bidId },
      }),
  });

  return (
    <Main className="flex flex-row">
      <div className="grow flex flex-col min-w-0">
        <MethodologyPage />
      </div>
      <VersionHistoryPanel />
    </Main>
  );
}

function MethodologyPage() {
  const { bidId, workspaceId, projectId } = Route.useParams();
  const search = useSearch({
    from: '/_authed/project/$projectId/workspaces/$workspaceId/bids/$bidId/methodology',
  });
  const navigate = useNavigate();
  const workspace = useWorkspace();
  const isClientView = useProjectView() === 'client';
  const draftVersionOffset = useDraftVersionOffset();
  const [createVersionOpen, setCreateVersionOpen] = useState(false);
  useBidMethodologyChat();

  const { areDocumentsIndexed: isTenderPackageIndexed, hasNoDocuments: tenderHasNoDocuments } =
    useDocumentsIndexingStatus({
      documentType: 'tenderPackage',
      id: workspaceId,
    });
  const { areDocumentsIndexed: isBidIndexed } = useDocumentsIndexingStatus({
    documentType: 'bid',
    id: bidId,
  });
  const areDocumentsIndexed = isTenderPackageIndexed && !tenderHasNoDocuments && isBidIndexed;

  const [toolbarPortal, setToolbarPortal] = useState<HTMLDivElement | null>(null);
  const exportDocx = useExportBidMethodologyDocument();
  const editor = useAtomValue(bidMethodologyEditorAtom);
  const editorDocId = useAtomValue(bidMethodologyEditorDocIdAtom);

  const { data: versions } = useQuery({
    ...bidMethodologyVersionsQuery(bidId),
    // poll while the live version is still analysing; its status drives completion
    refetchInterval: (query) => {
      const allVersions = query.state.data?.versions;
      const live = liveMethodologyVersion(allVersions);
      return live?.status === AnalysisStatus.IN_PROGRESS
        ? REFETCH_SUMMARIZATION_INTERVAL * 1000
        : false;
    },
  });
  const allVersions = versions?.versions ?? [];
  const selectedVersion = allVersions.find((v) => v.bidMethodologyId === search.version);
  const defaultVersion = liveMethodologyVersion(allVersions);

  const { data: bidDocuments } = useQuery(entityDocumentsQuery(bidId, 'bid'));
  const { allStatements } = useAllStatementsForVersion();
  const setSelectedSuggestionId = useSetAtom(selectedSuggestionIdAtom);
  const firstPending = (bidDocuments ?? [])
    .map((d) => buildSuggestionItems(allStatements, { selectedDocId: d.id, status: 'pending' })[0])
    .find(Boolean);
  const linkedStatement = allStatements.find((statement) => statement.id === search.statementId);

  useEffect(() => {
    if (!linkedStatement) return;
    const pending = [
      ...(linkedStatement.suggestions ?? []),
      ...(linkedStatement.blockedSuggestions ?? []),
      ...(linkedStatement.strategicSuggestions ?? []),
    ].find((item) => item.status === 'pending');
    setSelectedSuggestionId(isClientView ? linkedStatement.id : (pending?.id ?? null));
    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        tab: METHODOLOGY_TABS[1],
        docId: statementDocId(linkedStatement, pending) ?? prev.docId,
        statementId: undefined,
      }),
      replace: true,
    });
  }, [linkedStatement, isClientView, navigate, setSelectedSuggestionId]);

  if (!search.version && defaultVersion)
    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        tab: prev.commentThreadId ? 'editDocument' : prev.tab,
        version: prev.version ?? defaultVersion?.bidMethodologyId,
      }),
      replace: true,
    });

  const { isLoading } = useQuery(
    bidMethodologySummaryQuery(
      bidId,
      selectedVersion?.evaluationVersionId ?? undefined,
      selectedVersion?.bidMethodologyId,
    ),
  );

  const isSelectedRunning = !!selectedVersion && isRunningVersion(selectedVersion);

  useEffect(() => {
    if (!isSelectedRunning) return;
    navigate({
      to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId',
      params: { projectId, workspaceId, bidId },
      replace: true,
    });
  }, [isSelectedRunning, navigate, projectId, workspaceId, bidId]);
  const hasInProgress = defaultVersion?.status === AnalysisStatus.IN_PROGRESS;

  if (isLoading) return <Spinner kind="inset" />;

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId/bids/$bidId"
          params={{
            projectId,
            workspaceId,
            bidId,
          }}
        />
        <BidBreadcrumbs>
          <BreadcrumbPage>
            {t({ en: 'Execution plan review', nb: 'Gjennomføringsplan' })}
          </BreadcrumbPage>
        </BidBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <MainHeader className="justify-start">
        <H1 className="z-5">
          <MainHeaderBackButton
            to="/project/$projectId/workspaces/$workspaceId/bids/$bidId"
            params={{
              projectId,
              workspaceId,
              bidId,
            }}
          />
          {t({ en: 'Execution plan review', nb: 'Gjennomføringsplan' })}
        </H1>
        <Menu>
          <MenuTrigger
            render={
              <Button variant="secondary" size="sm" className="z-5 max-md:w-full md:mr-auto" />
            }
          >
            {selectedVersion ? (
              <span className="flex items-center gap-1.5">
                <span className="font-medium text-foreground">
                  {formatVersionLabel({
                    versionNumber: selectedVersion.versionNumber + draftVersionOffset,
                    displayName: selectedVersion.displayName,
                  })}
                </span>
                {selectedVersion.isLive ? (
                  <span className="inline-flex items-center gap-1 text-xs">
                    <BadgeDot variant="success" />
                    {t(common.versionHistory.currentVersion)}
                  </span>
                ) : null}
                {selectedVersion.status === AnalysisStatus.IN_PROGRESS ? (
                  <Spinner size="2xs" />
                ) : null}
              </span>
            ) : (
              t({ en: 'Select version', nb: 'Velg versjon' })
            )}
            <MenuTriggerIcon />
          </MenuTrigger>
          <MenuPopup align="start" scrollArea={false} className="min-w-40">
            <MenuScrollArea>
              <MenuGroup className="space-y-0.5">
                {allVersions.map((v) => (
                  <MenuItem
                    key={v.bidMethodologyId}
                    data-selected={
                      selectedVersion?.bidMethodologyId === v.bidMethodologyId ? '' : undefined
                    }
                    disabled={v?.status !== AnalysisStatus.DONE}
                    className="data-selected:bg-surface-3"
                    onClick={() =>
                      navigate({
                        to: '.',
                        search: (prev) => ({ ...prev, version: v.bidMethodologyId }),
                        replace: true,
                      })
                    }
                  >
                    <span className="flex items-center gap-1.5 flex-1">
                      <span className="font-medium text-foreground">
                        {formatVersionLabel({
                          versionNumber: v.versionNumber + draftVersionOffset,
                          displayName: v.displayName,
                        })}
                      </span>
                      {v.isLive ? (
                        <span className="inline-flex items-center gap-1 text-xs">
                          <BadgeDot variant="success" />
                          {t(common.versionHistory.currentVersion)}
                        </span>
                      ) : null}
                      {v.status === AnalysisStatus.IN_PROGRESS ? <Spinner size="xs" /> : null}
                    </span>
                  </MenuItem>
                ))}
              </MenuGroup>
            </MenuScrollArea>
            <MenuSeparator className="mx-0 my-0 w-full" />
            <MenuGroup className="p-(--popup-padding)">
              <MenuItem
                disabled={!areDocumentsIndexed || hasInProgress}
                onClick={() => setCreateVersionOpen(true)}
              >
                <Icon icon={PlusSignIcon} className="size-4" />
                {t({ en: 'Create new version', nb: 'Opprett ny versjon' })}
              </MenuItem>
            </MenuGroup>
          </MenuPopup>
        </Menu>
        {!isClientView &&
          search.tab === 'editDocument' &&
          editor &&
          editorDocId === search.docId && (
            <Button
              variant="secondary"
              className="max-md:w-full"
              onClick={() =>
                exportDocx.mutate(
                  `${workspace.name} ${t({ en: 'Bid development', nb: 'Tilbudsutvikling' }).toLowerCase()}`,
                )
              }
              pending={exportDocx.isPending}
            >
              <Icon icon={FileDownloadIcon} />
              {t({ en: 'Export as docx', nb: 'Eksporter som docx' })}
            </Button>
          )}
      </MainHeader>
      <CreateNewVersion
        open={createVersionOpen}
        onOpenChange={setCreateVersionOpen}
        onCreated={() =>
          navigate({
            to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId',
            params: { projectId, workspaceId, bidId },
          })
        }
      />
      <Tabs
        className={'grow min-h-0'}
        value={search.tab}
        onValueChange={(tab) => {
          navigate({
            to: '.',
            replace: true,
            search: (prev) => ({
              ...prev,
              tab,
              ...(tab === METHODOLOGY_TABS[1]
                ? {
                    docId:
                      (isClientView ? prev.docId : firstPending?.suggestion.targetDocId) ??
                      bidDocuments?.[0]?.id,
                  }
                : {}),
            }),
          });
          if (!isClientView && tab === METHODOLOGY_TABS[1] && firstPending)
            setSelectedSuggestionId(firstPending.suggestion.id);
        }}
      >
        <TabsList className={'px-x'}>
          {METHODOLOGY_TABS.map((tabValue) => (
            <TabsTab key={tabValue} value={tabValue}>
              {isClientView && tabValue === METHODOLOGY_TABS[1]
                ? t({ en: 'Document', nb: 'Dokument' })
                : t(
                    {
                      review: { en: 'Overview', nb: 'Gjennomgå' },
                      editDocument: { en: 'Edit plan', nb: 'Rediger plan' },
                      editDocumentClient: { en: 'Document', nb: 'Dokument' },
                    }[tabValue],
                  )}
            </TabsTab>
          ))}
        </TabsList>
        <TabsPanel value={METHODOLOGY_TABS[0]}>
          <BidMethodologyOverview />
        </TabsPanel>
        <TabsPanel value={METHODOLOGY_TABS[1]} className="relative">
          <MethodologyDocumentTabs />
          {isClientView ? null : <StaticFormattingToolbar setToolbarPortal={setToolbarPortal} />}
          <MainScrollArea>
            <MethodologyEditor docId={search.docId} toolbarPortal={toolbarPortal} />
          </MainScrollArea>
        </TabsPanel>
      </Tabs>
    </>
  );
}
